import * as Api from '@ViewModels';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { useContactCustomFieldsSuspenseQuery } from '../../../../../../../queries';
import { styleSheet as editContactStyles } from '../../../../../../components/contacts/EditContactInfo/styles';
import { EntityInfoFieldLabel } from '../../../../../../components/entities/EntityInfoFieldLabel';
import { EntityInfoSection } from '../../../../../../components/entities/EntityInfoSection';
import { TextInput } from '../../../../../../components/TextInput';
import { Button } from '../../../../../../components-v2/Button';
import { styleSheet } from '../styles';

type CustomFieldWithValue = Api.IFormField<string> & {
	value: string;
};

type Props = {
	contact: Api.IContact;
	onCustomFieldsChange: (customFields: CustomFieldWithValue[]) => void;
};

const getInitialValue = (customField: Api.IFormField<string>, contact: Api.IContact) => {
	if (contact.customProperties && contact.customProperties[customField.name]) {
		return contact.customProperties[customField.name];
	}
	return '';
};

const EditableCustomFieldsInner = ({ contact, onCustomFieldsChange }: Props) => {
	const customFieldsQuery = useContactCustomFieldsSuspenseQuery();

	const [customFields, setCustomFields] = React.useState<CustomFieldWithValue[]>(() =>
		(customFieldsQuery.data.fields ?? []).map(field => ({
			...field,
			value: getInitialValue(field, contact),
		}))
	);

	React.useEffect(() => {
		if (customFields.length > 0) {
			onCustomFieldsChange(customFields);
		}
	}, [customFields, onCustomFieldsChange]);

	const handleCustomFieldChange = (index: number, value: string) => {
		const newCustomFields = [...customFields];
		newCustomFields[index] = {
			...newCustomFields[index],
			value,
		};
		setCustomFields(newCustomFields);
	};

	if (customFields.length === 0) {
		return null;
	}

	return (
		<EntityInfoSection title='Custom Fields'>
			<div className={css(editContactStyles.fieldGroup)}>
				{customFields.map((customField, index) => (
					<div key={`custom-field-${customField.name}`}>
						<EntityInfoFieldLabel title={customField.label} />
						<TextInput
							autoComplete='off'
							inputId={`edit-custom-field-${customField.name}`}
							onChange={e => handleCustomFieldChange(index, e.target.value)}
							placeholder={customField.label}
							type='text'
							value={customField.value}
							className={css(styleSheet.editInput)}
						/>
					</div>
				))}
			</div>
		</EntityInfoSection>
	);
};

export const EditableCustomFields = (props: Props) => {
	const userSession = useUserSession();

	const hasCustomContactFields = userSession.account.additionalInfo?.hasCustomContactFields;

	if (!hasCustomContactFields) {
		return null;
	}

	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<ErrorBoundary
					onReset={reset}
					fallbackRender={({ resetErrorBoundary }) => (
						<EntityInfoSection title='Custom Fields'>
							<div className={css(s.error)}>
								Couldn’t load custom fields.{' '}
								<Button variant='link' type='button' onClick={() => resetErrorBoundary()}>
									Try again
								</Button>
							</div>
						</EntityInfoSection>
					)}
				>
					<React.Suspense fallback={<div>Loading custom fields...</div>}>
						<EditableCustomFieldsInner {...props} />
					</React.Suspense>
				</ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
};

const s = StyleSheet.create({
	error: {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
	},
});
